import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BEGINNER_TUTORIAL_SKIPPED_MESSAGE,
  BEGINNER_TUTORIAL_WELCOME,
  useTutorialContext
} from '../context/TutorialContext';
import './TutorialOverlay.css';

const TARGET_PADDING = 5;
const PANEL_WIDTH = 320;
const PANEL_GAP = 14;
const VIEWPORT_MARGIN = 14;
const DEFAULT_PANEL_SIZE = { width: PANEL_WIDTH, height: 220 };

const getCutoutPath = (rects) => {
  const holes = rects
    .filter(Boolean)
    .map(rect => `M ${rect.left} ${rect.top} H ${rect.right} V ${rect.bottom} H ${rect.left} Z`)
    .join(' ');

  return `M 0 0 H ${window.innerWidth} V ${window.innerHeight} H 0 Z ${holes}`;
};

const getPaddedRect = (rect) => ({
  top: Math.max(VIEWPORT_MARGIN, rect.top - TARGET_PADDING),
  left: Math.max(VIEWPORT_MARGIN, rect.left - TARGET_PADDING),
  right: Math.min(window.innerWidth - VIEWPORT_MARGIN, rect.right + TARGET_PADDING),
  bottom: Math.min(window.innerHeight - VIEWPORT_MARGIN, rect.bottom + TARGET_PADDING)
});

const clamp = (value, min, max) => {
  const safeMax = Math.max(min, max);
  return Math.min(Math.max(value, min), safeMax);
};

const rectsOverlap = (rectA, rectB) => (
  rectA.left < rectB.right
  && rectA.right > rectB.left
  && rectA.top < rectB.bottom
  && rectA.bottom > rectB.top
);

const shouldScrollTargetIntoView = (rect) => {
  const targetFitsInViewport = rect.height < window.innerHeight - VIEWPORT_MARGIN * 2;
  const targetIsOutOfView = (
    rect.top < VIEWPORT_MARGIN
    || rect.left < VIEWPORT_MARGIN
    || rect.bottom > window.innerHeight - VIEWPORT_MARGIN
    || rect.right > window.innerWidth - VIEWPORT_MARGIN
  );

  return targetFitsInViewport && targetIsOutOfView;
};

const getPanelPosition = (targetRect, panelSize = DEFAULT_PANEL_SIZE) => {
  const width = Math.min(panelSize.width || PANEL_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
  const height = Math.min(panelSize.height || DEFAULT_PANEL_SIZE.height, window.innerHeight - VIEWPORT_MARGIN * 2);
  const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
  const maxTop = window.innerHeight - height - VIEWPORT_MARGIN;

  if (!targetRect) {
    return {
      top: clamp(window.innerHeight / 2 - height / 2, VIEWPORT_MARGIN, maxTop),
      left: clamp(window.innerWidth / 2 - width / 2, VIEWPORT_MARGIN, maxLeft)
    };
  }

  const targetCenterX = targetRect.left + ((targetRect.right - targetRect.left) / 2);
  const targetCenterY = targetRect.top + ((targetRect.bottom - targetRect.top) / 2);
  const positions = [
    {
      name: 'right',
      top: clamp(targetCenterY - height / 2, VIEWPORT_MARGIN, maxTop),
      left: targetRect.right + PANEL_GAP
    },
    {
      name: 'left',
      top: clamp(targetCenterY - height / 2, VIEWPORT_MARGIN, maxTop),
      left: targetRect.left - PANEL_GAP - width
    },
    {
      name: 'below',
      top: targetRect.bottom + PANEL_GAP,
      left: clamp(targetCenterX - width / 2, VIEWPORT_MARGIN, maxLeft)
    },
    {
      name: 'above',
      top: targetRect.top - PANEL_GAP - height,
      left: clamp(targetCenterX - width / 2, VIEWPORT_MARGIN, maxLeft)
    }
  ];

  const candidates = positions.map(position => ({
    ...position,
    top: clamp(position.top, VIEWPORT_MARGIN, maxTop),
    left: clamp(position.left, VIEWPORT_MARGIN, maxLeft)
  }));

  const nonOverlappingCandidate = candidates.find((candidate) => {
    const panelRect = {
      top: candidate.top,
      left: candidate.left,
      right: candidate.left + width,
      bottom: candidate.top + height
    };
    return !rectsOverlap(panelRect, targetRect);
  });

  return nonOverlappingCandidate || candidates[0];
};

const TutorialOverlay = () => {
  const {
    beginnerStep,
    beginnerStepId,
    advanceBeginnerTutorial,
    closeBeginnerTutorialSkippedMessage,
    isBeginnerTutorialSkippedMessageOpen,
    isBeginnerTutorialActive,
    isBeginnerTutorialWelcomeOpen,
    recoverMissingTutorialTarget,
    skipBeginnerTutorial,
    startBeginnerTutorial
  } = useTutorialContext();
  const [targetRect, setTargetRect] = useState(null);
  const [interactionRect, setInteractionRect] = useState(null);
  const [secondaryTargetRect, setSecondaryTargetRect] = useState(null);
  const [targetFound, setTargetFound] = useState(false);
  const [panelSize, setPanelSize] = useState(DEFAULT_PANEL_SIZE);
  const panelRef = useRef(null);
  const missingTargetTimerRef = useRef(null);

  useEffect(() => {
    if (!isBeginnerTutorialActive || !beginnerStep) return undefined;

    let isMounted = true;
    const targetSelector = beginnerStep.spotlightSelector || beginnerStep.targetSelector;
    const interactionSelector = beginnerStep.targetSelector || targetSelector;
    const clearMissingTargetTimer = () => {
      if (missingTargetTimerRef.current) {
        window.clearTimeout(missingTargetTimerRef.current);
        missingTargetTimerRef.current = null;
      }
    };
    const updateTarget = () => {
      const secondaryTarget = beginnerStep.secondarySpotlightSelector
        ? document.querySelector(beginnerStep.secondarySpotlightSelector)
        : null;

      if (!targetSelector) {
        clearMissingTargetTimer();
        setTargetFound(false);
        setTargetRect(null);
        setInteractionRect(null);
        setSecondaryTargetRect(secondaryTarget ? getPaddedRect(secondaryTarget.getBoundingClientRect()) : null);
        return;
      }

      const target = document.querySelector(targetSelector);
      const interactionTarget = interactionSelector ? document.querySelector(interactionSelector) : target;
      if (!isMounted) return;

      if (!target) {
        setTargetFound(false);
        setTargetRect(null);
        setInteractionRect(null);
        setSecondaryTargetRect(null);
        if (beginnerStep.missingTargetStep && !missingTargetTimerRef.current) {
          missingTargetTimerRef.current = window.setTimeout(() => {
            recoverMissingTutorialTarget?.(beginnerStepId);
            missingTargetTimerRef.current = null;
          }, 600);
        }
        return;
      }

      clearMissingTargetTimer();
      const rawTargetRect = target.getBoundingClientRect();
      if (shouldScrollTargetIntoView(rawTargetRect)) {
        target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      setTargetFound(true);
      setTargetRect(getPaddedRect(target.getBoundingClientRect()));
      setInteractionRect(interactionTarget ? getPaddedRect(interactionTarget.getBoundingClientRect()) : getPaddedRect(target.getBoundingClientRect()));
      setSecondaryTargetRect(secondaryTarget ? getPaddedRect(secondaryTarget.getBoundingClientRect()) : null);
    };

    updateTarget();
    const interval = window.setInterval(updateTarget, 180);
    window.addEventListener('resize', updateTarget);
    window.addEventListener('scroll', updateTarget, true);

    return () => {
      isMounted = false;
      clearMissingTargetTimer();
      window.clearInterval(interval);
      window.removeEventListener('resize', updateTarget);
      window.removeEventListener('scroll', updateTarget, true);
    };
  }, [beginnerStep, beginnerStepId, isBeginnerTutorialActive, recoverMissingTutorialTarget]);

  useEffect(() => {
    if (!isBeginnerTutorialActive || !beginnerStep?.advanceOnTargetClick) return undefined;

    const handleDocumentClick = (event) => {
      const target = document.querySelector(beginnerStep.targetSelector);
      if (!target || !target.contains(event.target)) return;
      window.setTimeout(advanceBeginnerTutorial, 80);
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [advanceBeginnerTutorial, beginnerStep, isBeginnerTutorialActive]);

  useEffect(() => {
    if (!isBeginnerTutorialActive || beginnerStep?.targetMode !== 'form-task') return undefined;

    const handleDocumentEvent = (event) => {
      if (panelRef.current?.contains(event.target)) return;

      const allowedSelectors = [
        beginnerStep.targetSelector,
        beginnerStep.spotlightSelector,
        beginnerStep.secondarySpotlightSelector
      ].filter(Boolean);
      const isAllowed = allowedSelectors.some((selector) => {
        const target = document.querySelector(selector);
        return target?.contains(event.target);
      });

      if (!isAllowed) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('click', handleDocumentEvent, true);
    document.addEventListener('mousedown', handleDocumentEvent, true);
    document.addEventListener('keydown', handleDocumentEvent, true);
    return () => {
      document.removeEventListener('click', handleDocumentEvent, true);
      document.removeEventListener('mousedown', handleDocumentEvent, true);
      document.removeEventListener('keydown', handleDocumentEvent, true);
    };
  }, [beginnerStep, isBeginnerTutorialActive]);

  const blockerStyle = useMemo(() => {
    if (!interactionRect) {
      return {
        top: { inset: 0 },
        left: { display: 'none' },
        right: { display: 'none' },
        bottom: { display: 'none' }
      };
    }

    return {
      top: {
        top: 0,
        left: 0,
        right: 0,
        height: `${interactionRect.top}px`
      },
      left: {
        top: `${interactionRect.top}px`,
        left: 0,
        width: `${interactionRect.left}px`,
        height: `${Math.max(0, interactionRect.bottom - interactionRect.top)}px`
      },
      right: {
        top: `${interactionRect.top}px`,
        left: `${interactionRect.right}px`,
        right: 0,
        height: `${Math.max(0, interactionRect.bottom - interactionRect.top)}px`
      },
      bottom: {
        top: `${interactionRect.bottom}px`,
        left: 0,
        right: 0,
        bottom: 0
      }
    };
  }, [interactionRect]);

  useLayoutEffect(() => {
    if (!panelRef.current) return;
    const measuredSize = {
      width: panelRef.current.offsetWidth,
      height: panelRef.current.offsetHeight
    };

    setPanelSize(currentSize => (
      currentSize.width === measuredSize.width && currentSize.height === measuredSize.height
        ? currentSize
        : measuredSize
    ));
  }, [beginnerStep, targetFound]);

  useEffect(() => {
    if (!isBeginnerTutorialActive || !beginnerStep) return;

    window.setTimeout(() => {
      let focusTarget = null;
      if (beginnerStep.initialFocusSelector) {
        focusTarget = document.querySelector(beginnerStep.initialFocusSelector);
      } else if (beginnerStep.nextLabel && panelRef.current) {
        focusTarget = panelRef.current.querySelector('button');
      } else if (beginnerStep.targetSelector) {
        focusTarget = document.querySelector(beginnerStep.targetSelector);
      }

      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    }, 0);
  }, [beginnerStep, isBeginnerTutorialActive]);

  if (isBeginnerTutorialWelcomeOpen) {
    return (
      <div className="tutorial-layer tutorial-modal-layer" aria-live="polite">
        <div className="tutorial-full-blocker" />
        <section className="tutorial-panel tutorial-centered-panel" role="dialog" aria-modal="true" aria-label={BEGINNER_TUTORIAL_WELCOME.title}>
          <h2>{BEGINNER_TUTORIAL_WELCOME.title}</h2>
          <p>{BEGINNER_TUTORIAL_WELCOME.body}</p>
          <p>{BEGINNER_TUTORIAL_WELCOME.question}</p>
          <div className="tutorial-actions tutorial-actions-right">
            <button type="button" onClick={() => startBeginnerTutorial()}>
              {BEGINNER_TUTORIAL_WELCOME.startLabel}
            </button>
            <button type="button" className="secondary-button" onClick={skipBeginnerTutorial}>
              {BEGINNER_TUTORIAL_WELCOME.skipLabel}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (isBeginnerTutorialSkippedMessageOpen) {
    return (
      <div className="tutorial-layer tutorial-modal-layer" aria-live="polite">
        <div className="tutorial-full-blocker" />
        <section className="tutorial-panel tutorial-centered-panel" role="dialog" aria-modal="true" aria-label={BEGINNER_TUTORIAL_SKIPPED_MESSAGE.title}>
          <h2>{BEGINNER_TUTORIAL_SKIPPED_MESSAGE.title}</h2>
          <p>{BEGINNER_TUTORIAL_SKIPPED_MESSAGE.body}</p>
          <div className="tutorial-actions tutorial-actions-right">
            <button type="button" onClick={closeBeginnerTutorialSkippedMessage}>
              {BEGINNER_TUTORIAL_SKIPPED_MESSAGE.closeLabel}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (!isBeginnerTutorialActive || !beginnerStep) return null;

  const isCompletionStep = beginnerStep.targetMode === 'completion';
  const isFormTaskStep = beginnerStep.targetMode === 'form-task';
  const hasSpotlight = Boolean(targetRect) && !isCompletionStep;
  const hasSecondarySpotlight = Boolean(secondaryTargetRect) && !isCompletionStep;
  const blocksTarget = hasSpotlight && (
    beginnerStep.blockTargetInteraction
    || beginnerStep.targetMode === 'review'
  );
  const panelPosition = getPanelPosition(hasSpotlight ? targetRect : null, panelSize);
  const instructionText = targetFound || beginnerStep.nextLabel
    ? beginnerStep.instruction
    : 'Looking for the next step...';

  return (
    <div className={`tutorial-layer tutorial-mode-${beginnerStep.targetMode || 'action'}`} aria-live="polite">
      {isFormTaskStep && (hasSpotlight || hasSecondarySpotlight) ? (
        <svg
          className="tutorial-cutout-backdrop"
          aria-hidden="true"
          width={window.innerWidth}
          height={window.innerHeight}
          viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}
        >
          <path d={getCutoutPath([targetRect, secondaryTargetRect])} fillRule="evenodd" />
        </svg>
      ) : (
        <>
          <div className="tutorial-blocker" style={blockerStyle.top} />
          <div className="tutorial-blocker" style={blockerStyle.left} />
          <div className="tutorial-blocker" style={blockerStyle.right} />
          <div className="tutorial-blocker" style={blockerStyle.bottom} />
        </>
      )}

      {hasSpotlight && (
        <div
          className="tutorial-spotlight"
          style={{
            top: `${targetRect.top}px`,
            left: `${targetRect.left}px`,
            width: `${Math.max(0, targetRect.right - targetRect.left)}px`,
            height: `${Math.max(0, targetRect.bottom - targetRect.top)}px`
          }}
        />
      )}

      {hasSecondarySpotlight && (
        <div
          className="tutorial-spotlight tutorial-secondary-spotlight"
          style={{
            top: `${secondaryTargetRect.top}px`,
            left: `${secondaryTargetRect.left}px`,
            width: `${Math.max(0, secondaryTargetRect.right - secondaryTargetRect.left)}px`,
            height: `${Math.max(0, secondaryTargetRect.bottom - secondaryTargetRect.top)}px`
          }}
        />
      )}

      {blocksTarget && (
        <div
          className="tutorial-target-blocker"
          aria-hidden="true"
          style={{
            top: `${targetRect.top}px`,
            left: `${targetRect.left}px`,
            width: `${Math.max(0, targetRect.right - targetRect.left)}px`,
            height: `${Math.max(0, targetRect.bottom - targetRect.top)}px`
          }}
        />
      )}

      <section
        ref={panelRef}
        className="tutorial-panel"
        role="dialog"
        aria-label="Beginner tutorial"
        style={{
          top: `${panelPosition.top}px`,
          left: `${panelPosition.left}px`
        }}
      >
        <h2>{beginnerStep.title}</h2>
        <p>{beginnerStep.body}</p>
        <p className="tutorial-instruction">{instructionText}</p>
        <div className="tutorial-actions tutorial-actions-right">
          {beginnerStep.nextLabel && (
            <button type="button" onClick={advanceBeginnerTutorial}>
              {beginnerStep.nextLabel}
            </button>
          )}
          {!beginnerStep.hideSkip && (
            <button type="button" className="secondary-button" onClick={skipBeginnerTutorial}>
              Skip tutorial
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default TutorialOverlay;
